import * as preact from "preact";

import Node, { NodeMirror } from "../Node";
import { DragSource, DropTarget } from "../../../DragDropContainer";

import classname from "../../../../foundation/utils/classname";
import noderef from "../../../../foundation/utils/noderef";

const ITEM_TYPE = {
    STYLEGUIDE_SECTION_NODE: "StyleguideSectionNode",
    STYLEGUIDE_COMPONENT: "StyleguideComponent"
};

const NodeLevel = {
    DEFAULT_SECTION: 0,
    COMPONENT_SECTION: 1,
    COMPONENT_GROUP: 2
};

const RawDropNode = DropTarget({
    acceptsItemType: [
        ITEM_TYPE.STYLEGUIDE_SECTION_NODE,
        ITEM_TYPE.STYLEGUIDE_COMPONENT
    ],
    onHover: (props, dragState) => props.onHover(
        {
            top: dragState.mirrorPosition.top,
            left: dragState.mirrorPosition.left
        },
        dragState
    ),
    onHoverOut: (props, dragState) => props.onHoverOut(dragState.draggingItemType),
    onDrop(props, dragState) {
        const { idPath, index } = dragState.dragData;
        props.onDrop({ idPath, index }, dragState);
    }
})(Node);

const RawDragDropNode = DragSource({
    itemType: ITEM_TYPE.STYLEGUIDE_SECTION_NODE,
    mirrorComponent: NodeMirror,
    provideDragData: props => props,
    onStartDrag: props => {
        const { id, onStartDrag } = props;
        onStartDrag(id);
    }
})(RawDropNode);

class DragDropNode extends preact.Component {
    state = {
        hovered: false,
        targetPosition: ""
    };

    setTargetPosition = (pointerPosition, dragNodeLevel) => {
        const { level, numberOfChildren, lastNode, collapsed } = this.props;
        const { targetPosition } = this.state;

        if (dragNodeLevel === NodeLevel.COMPONENT_GROUP && level === NodeLevel.DEFAULT_SECTION) {
            return;
        }

        // If default section hovered, show target position on bottom
        if (level === NodeLevel.DEFAULT_SECTION) {
            this.setState({
                targetPosition: "bottom"
            });
            return;
        }

        // If a section has groups under it and it is not collapsed, show target position always on top
        if (level === NodeLevel.COMPONENT_SECTION && dragNodeLevel === level && numberOfChildren && !collapsed) {
            this.setState({
                targetPosition: "top"
            });
            return;
        }

        // If dragging node is section but the hovered one is group, and the hovered
        // one is the last node, show target position on bottom
        if (dragNodeLevel === NodeLevel.COMPONENT_SECTION && level === NodeLevel.COMPONENT_GROUP) {
            if (lastNode) {
                this.setState({
                    targetPosition: "bottom"
                });
            }
            return;
        }

        if (dragNodeLevel === NodeLevel.COMPONENT_GROUP && level === NodeLevel.COMPONENT_SECTION) {
            this.setState({
                targetPosition: "into"
            });
            return;
        }

        const baseRect = this.nodeRefs.base.getBoundingClientRect();
        const baseCenter = baseRect.top + (baseRect.height / 2);
        const mirrorCenter = pointerPosition.top;
        const targetToBottom = mirrorCenter >= baseCenter;
        const targetPositionText = targetToBottom ? "bottom" : "top";

        if (targetPosition !== targetPositionText) {
            this.setState({
                targetPosition: targetPositionText
            });
        }
    }

    calculateMoveIndex = (sourceIndex, targetIndex, targetToBottom) => {
        let moveIndex = targetIndex;
        if (sourceIndex > moveIndex && targetToBottom) {
            moveIndex++;
        } else if (sourceIndex < moveIndex && !targetToBottom) {
            moveIndex--;
        }

        return moveIndex;
    }

    unsetTargetPosition = () => {
        if (this.state.targetPosition) {
            this.setState({
                targetPosition: ""
            });
        }
    }

    setSectionHeaderHover = dragNodeStyleguideId => {
        const { barrelId } = this.context;
        const { styleguideId: nodeStyleguideId } = this.props;
        if (barrelId === nodeStyleguideId && barrelId === dragNodeStyleguideId) {
            this.setState({
                hovered: true
            });
        }
    }

    unsetSectionHeaderHover = () => {
        if (this.state.hovered) {
            this.setState({
                hovered: false
            });
        }
    }

    handleComponentDrop = (componentIdPath, componentIndex) => {
        if (!this.state.hovered) {
            return;
        }
        const { idPath: componentSectionIdPath, onComponentToSectionNodeDrop } = this.props;
        onComponentToSectionNodeDrop(componentIdPath, componentSectionIdPath, componentIndex);
    }

    handleNodeDrop = (sourceIdPath, sourceIndex, draggingNodeLevel) => {
        const {
            id: targetId,
            idPath: targetIdPath,
            index: targetIndex,
            parentIndex: targetParentIndex,
            level: targetNodeLevel,
            numberOfChildren,
            onReorderComponentSectionOrGroup
        } = this.props;
        const { targetPosition } = this.state;

        if (!targetPosition) {
            return;
        }

        const targetToBottom = targetPosition === "bottom";
        const componentSection = {
            csid: sourceIdPath,
            sourceIndex
        };

        if (draggingNodeLevel === NodeLevel.COMPONENT_SECTION && targetNodeLevel === NodeLevel.DEFAULT_SECTION) {
            // section to default section drop
            if (sourceIndex === 1) {
                return;
            }
            componentSection.to = 1;
        } else if (
            draggingNodeLevel === NodeLevel.COMPONENT_SECTION &&
            targetNodeLevel === NodeLevel.COMPONENT_SECTION
        ) {
            // section to section drop
            const moveTo = this.calculateMoveIndex(sourceIndex, targetIndex, targetToBottom);
            if (moveTo === sourceIndex) {
                return;
            }

            componentSection.to = moveTo;
        } else if (
            draggingNodeLevel === NodeLevel.COMPONENT_SECTION &&
            targetNodeLevel === NodeLevel.COMPONENT_GROUP
        ) {
            // section to group drop(this happens if the source section node
            // tried to put under an expanded section which has groups under it)
            const moveTo = this.calculateMoveIndex(sourceIndex, targetParentIndex, targetToBottom);
            if (moveTo === sourceIndex) {
                return;
            }

            componentSection.to = moveTo;
        } else if (
            draggingNodeLevel === NodeLevel.COMPONENT_GROUP &&
            targetNodeLevel === NodeLevel.COMPONENT_SECTION
        ) {
            // group to section drop(group will be in the last index under section)
            if (targetId === sourceIdPath[0]) {
                return;
            }

            componentSection.tcsid = targetIdPath;
            componentSection.to = numberOfChildren;
        } else if (draggingNodeLevel === NodeLevel.COMPONENT_GROUP && targetNodeLevel === NodeLevel.COMPONENT_GROUP) {
            // group to group drop
            const [sourceSectionId] = sourceIdPath;
            const [targetSectionId] = targetIdPath;

            const moveTo = sourceSectionId === targetSectionId
                ? this.calculateMoveIndex(sourceIndex, targetIndex, targetToBottom) - 1
                : targetIndex - Number(!targetToBottom);

            if (sourceSectionId === targetSectionId && moveTo === sourceIndex - 1) {
                return;
            }

            componentSection.to = moveTo;
            componentSection.tcsid = [targetSectionId];
        }

        onReorderComponentSectionOrGroup(componentSection);
    }

    handleDragHover = (pointerPosition, dragState) => {
        const { draggingItemType, dragData } = dragState;
        if (draggingItemType === ITEM_TYPE.STYLEGUIDE_SECTION_NODE) {
            this.setTargetPosition(pointerPosition, dragData.level);
        } else if (draggingItemType === ITEM_TYPE.STYLEGUIDE_COMPONENT) {
            this.setSectionHeaderHover(dragData.styleguideId);
        }
    }

    handleDragHoverOut = draggingItemType => {
        if (draggingItemType === ITEM_TYPE.STYLEGUIDE_SECTION_NODE) {
            this.unsetTargetPosition();
        } else if (draggingItemType === ITEM_TYPE.STYLEGUIDE_COMPONENT) {
            this.unsetSectionHeaderHover();
        }
    }

    handleDrop = ({ idPath, index }, dragState) => {
        const { draggingItemType, dragData: { level } } = dragState;
        if (draggingItemType === ITEM_TYPE.STYLEGUIDE_SECTION_NODE) {
            this.handleNodeDrop(idPath, index, level);
        } else if (draggingItemType === ITEM_TYPE.STYLEGUIDE_COMPONENT) {
            this.handleComponentDrop(idPath, index);
        }
    }

    render() {
        const { barrelId } = this.context;
        const { hovered, targetPosition } = this.state;
        const { class: nodeClass, level, ...otherProps } = this.props;

        const RawNode = level === NodeLevel.DEFAULT_SECTION ? RawDropNode : RawDragDropNode;

        return barrelId === this.props.styleguideId ? (
            <RawNode
                {...otherProps}
                level={level}
                class={classname("dragDropNode", nodeClass, { dropHover: hovered || targetPosition === "into" })}
                baseRef={noderef(this, "base")}
                targetPosition={targetPosition}
                onHover={this.handleDragHover}
                onHoverOut={this.handleDragHoverOut}
                onDrop={this.handleDrop}/>
        ) : (
            <Node {...this.props}/>
        );
    }
}

export default DragDropNode;
